/**
 * RoutePreview - Shows route details before starting navigation
 * Includes route priority toggle (Fastest vs Safest Air)
 * Uses Mapbox Directions API for real road-following routes
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  useNavigationStore,
  generateRouteOptions,
  type RoutePriority,
  type RouteOption,
} from '@/stores/useNavigationStore';
import { useLocationStore, useAirQualityStore, selectNeuralIndex } from '@/stores';
import { GlassPanel } from '@/components/common';
import { flyToLocation } from '@/utils/mapInstance';
import { fetchRouteOptions } from '@/services/routing/routeService';
import { cn } from '@/utils/cn';

interface RoutePreviewProps {
  className?: string;
}

export function RoutePreview({ className }: RoutePreviewProps) {
  const selectedDestination = useNavigationStore((state) => state.selectedDestination);
  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const routePriority = useNavigationStore((state) => state.routePriority);
  const routeOptions = useNavigationStore((state) => state.routeOptions);
  const selectedRouteId = useNavigationStore((state) => state.selectedRouteId);

  const setRoutePriority = useNavigationStore((state) => state.setRoutePriority);
  const setRouteOptions = useNavigationStore((state) => state.setRouteOptions);
  const startNavigation = useNavigationStore((state) => state.startNavigation);
  const clearSearch = useNavigationStore((state) => state.clearSearch);

  const currentPosition = useLocationStore((state) => state.currentPosition);
  const currentNI = useAirQualityStore(selectNeuralIndex);

  const [isLoadingRoute, setIsLoadingRoute] = useState(false);

  // Fetch real routes from Mapbox Directions API
  useEffect(() => {
    if (!selectedDestination || !currentPosition) return;

    let cancelled = false;
    setIsLoadingRoute(true);

    // Try to fetch real routes from Mapbox
    fetchRouteOptions(currentPosition, selectedDestination, currentNI)
      .then((options) => {
        if (cancelled) return;

        if (options.length > 0) {
          // Use real Mapbox routes
          setRouteOptions(options);
        } else {
          // Fallback to generated routes if API fails
          const fallbackOptions = generateRouteOptions(currentPosition, selectedDestination, currentNI);
          setRouteOptions(fallbackOptions);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Fallback on error
        const fallbackOptions = generateRouteOptions(currentPosition, selectedDestination, currentNI);
        setRouteOptions(fallbackOptions);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRoute(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedDestination, currentPosition, currentNI, setRouteOptions]);

  if (!selectedDestination || isNavigating) {
    return null;
  }

  // Get selected route
  const selectedRoute = routeOptions.find((r) => r.id === selectedRouteId);
  const fastestRoute = routeOptions.find((r) => r.priority === 'fastest');
  const safestRoute = routeOptions.find((r) => r.priority === 'safest');

  const handleStartNavigation = () => {
    if (!selectedRoute) return;

    // Auto-recenter map to user location with navigation view
    if (currentPosition) {
      flyToLocation(currentPosition.lat, currentPosition.lng, 17);
    }

    // Start navigation
    startNavigation(selectedRoute);
  };

  const handlePriorityChange = (priority: RoutePriority) => {
    setRoutePriority(priority);
  };

  // Show loading state
  if (isLoadingRoute || !selectedRoute) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn('w-full max-w-md', className)}
      >
        <GlassPanel className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-hud-primary/20 flex items-center justify-center">
              <DestinationIcon className="w-5 h-5 text-hud-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-hud text-sm text-hud-primary uppercase tracking-wider">
                {selectedDestination.name}
              </h3>
              <p className="text-xs text-text-secondary mt-0.5">
                Finding best route...
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-2 border-hud-primary/30 border-t-hud-primary rounded-full animate-spin" />
          </div>
        </GlassPanel>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={cn('w-full max-w-md', className)}
    >
      <GlassPanel className="space-y-4">
        {/* Destination Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-hud-primary/20 flex items-center justify-center flex-shrink-0">
            <DestinationIcon className="w-5 h-5 text-hud-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-hud text-sm text-hud-primary uppercase tracking-wider">
              {selectedDestination.name}
            </h3>
            <p className="text-xs text-text-secondary mt-0.5 truncate">
              {selectedDestination.address}
            </p>
          </div>
          <button
            onClick={clearSearch}
            className="p-1.5 text-text-secondary hover:text-hud-primary transition-colors"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Route Priority Toggle */}
        <div className="flex gap-2 p-1 bg-bg-surface rounded-lg">
          <PriorityButton
            active={routePriority === 'fastest'}
            onClick={() => handlePriorityChange('fastest')}
            icon={<SpeedIcon className="w-4 h-4" />}
            label="Fastest"
            subtitle={fastestRoute ? formatDuration(fastestRoute.totalDuration) : '--'}
          />
          <PriorityButton
            active={routePriority === 'safest'}
            onClick={() => handlePriorityChange('safest')}
            icon={<LeafIcon className="w-4 h-4" />}
            label="Safest Air"
            subtitle={safestRoute ? `NI ${safestRoute.averageNI}%` : '--'}
            recommended
          />
        </div>

        {/* Route Stats */}
        <div className="grid grid-cols-3 gap-3">
          <StatBox
            label="Distance"
            value={formatDistance(selectedRoute.totalDistance)}
            icon={<DistanceIcon className="w-4 h-4" />}
          />
          <StatBox
            label="Walk Time"
            value={formatDuration(selectedRoute.totalDuration)}
            icon={<TimeIcon className="w-4 h-4" />}
          />
          <StatBox
            label="Arrival NI"
            value={`${selectedRoute.arrivalNeuralIndex}%`}
            icon={<BrainIcon className="w-4 h-4" />}
            highlight={selectedRoute.arrivalNeuralIndex >= 80}
          />
        </div>

        {/* Route Comparison */}
        {fastestRoute && safestRoute && (
          <RouteComparison fastest={fastestRoute} safest={safestRoute} selected={routePriority} />
        )}

        {/* Cognitive Route Info */}
        <div
          className={cn(
            'p-3 rounded-lg border',
            routePriority === 'safest'
              ? 'bg-hud-success/10 border-hud-success/30'
              : 'bg-hud-warning/10 border-hud-warning/30'
          )}
        >
          <div
            className={cn(
              'flex items-center gap-2',
              routePriority === 'safest' ? 'text-hud-success' : 'text-hud-warning'
            )}
          >
            {routePriority === 'safest' ? (
              <LeafIcon className="w-4 h-4" />
            ) : (
              <SpeedIcon className="w-4 h-4" />
            )}
            <span className="text-xs font-hud uppercase tracking-wider">
              {routePriority === 'safest' ? 'Low Cognitive Debt Route' : 'Fastest Route'}
            </span>
          </div>
          <p className="text-xs text-text-secondary mt-1">
            {routePriority === 'safest'
              ? 'This route prioritizes parks and backstreets to minimize air pollution exposure.'
              : 'This is the quickest path but may have higher pollution exposure on main roads.'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={clearSearch}
            className={cn(
              'flex-1 py-3 px-4 rounded-lg',
              'bg-glass-bg border border-glass-border',
              'text-text-secondary text-sm font-hud uppercase tracking-wider',
              'hover:bg-hud-primary/10 hover:text-hud-primary transition-colors'
            )}
          >
            Cancel
          </button>
          <button
            onClick={handleStartNavigation}
            className={cn(
              'flex-1 py-3 px-4 rounded-lg',
              'text-sm font-hud uppercase tracking-wider font-bold',
              'transition-colors',
              'flex items-center justify-center gap-2',
              'bg-hud-primary text-bg-deep hover:bg-hud-primary/90 shadow-hud-glow'
            )}
          >
            <NavigateIcon className="w-4 h-4" />
            Start Walk
          </button>
        </div>
      </GlassPanel>
    </motion.div>
  );
}

interface PriorityButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  subtitle: string;
  recommended?: boolean;
}

function PriorityButton({
  active,
  onClick,
  icon,
  label,
  subtitle,
  recommended,
}: PriorityButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 py-2 px-3 rounded-lg transition-all relative',
        active
          ? 'bg-hud-primary/20 border border-hud-primary/50 text-hud-primary'
          : 'bg-transparent border border-transparent text-text-secondary hover:bg-glass-bg'
      )}
    >
      {recommended && !active && (
        <span className="absolute -top-1 -right-1 text-[8px] bg-hud-success text-bg-deep px-1.5 rounded-full font-bold">
          REC
        </span>
      )}
      <div className="flex items-center justify-center gap-1.5">
        {icon}
        <span className="text-xs font-hud uppercase">{label}</span>
      </div>
      <div className="text-[10px] text-text-muted mt-0.5">{subtitle}</div>
    </button>
  );
}

interface RouteComparisonProps {
  fastest: RouteOption;
  safest: RouteOption;
  selected: RoutePriority;
}

function RouteComparison({ fastest, safest, selected }: RouteComparisonProps) {
  const timeDiff = safest.totalDuration - fastest.totalDuration;
  const distDiff = safest.totalDistance - fastest.totalDistance;
  const niDiff = safest.averageNI - fastest.averageNI;

  return (
    <div className="text-xs text-text-secondary bg-bg-surface/50 rounded-lg p-2">
      <div className="flex items-center justify-between">
        <span>Route Comparison:</span>
        <span
          className={cn(
            'font-data',
            selected === 'safest' ? 'text-hud-success' : 'text-hud-warning'
          )}
        >
          {selected === 'safest' ? 'Cleaner Air' : 'Faster'}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        <div className="text-center">
          <div className="text-text-muted text-[10px]">Time</div>
          <div className={timeDiff > 0 ? 'text-hud-warning' : 'text-hud-success'}>
            {timeDiff > 0 ? '+' : ''}
            {Math.round(timeDiff / 60)}min
          </div>
        </div>
        <div className="text-center">
          <div className="text-text-muted text-[10px]">Distance</div>
          <div className={distDiff > 0 ? 'text-hud-warning' : 'text-hud-success'}>
            {distDiff > 0 ? '+' : ''}
            {Math.round(distDiff)}m
          </div>
        </div>
        <div className="text-center">
          <div className="text-text-muted text-[10px]">Air Quality</div>
          <div className={niDiff > 0 ? 'text-hud-success' : 'text-hud-warning'}>
            {niDiff > 0 ? '+' : ''}
            {niDiff}% NI
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatBoxProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  highlight?: boolean;
}

function StatBox({ label, value, icon, highlight }: StatBoxProps) {
  return (
    <div
      className={cn('p-2 rounded-lg text-center', 'bg-bg-surface border border-glass-border')}
    >
      <div
        className={cn(
          'flex items-center justify-center gap-1 mb-1',
          highlight ? 'text-hud-success' : 'text-hud-primary'
        )}
      >
        {icon}
      </div>
      <div
        className={cn(
          'text-lg font-data font-bold',
          highlight ? 'text-hud-success' : 'text-text-primary'
        )}
      >
        {value}
      </div>
      <div className="text-[10px] text-text-secondary uppercase">{label}</div>
    </div>
  );
}

function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

// Icons
function DestinationIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function DistanceIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function TimeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function BrainIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
    </svg>
  );
}

function LeafIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </svg>
  );
}

function SpeedIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function NavigateIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" />
    </svg>
  );
}

export default RoutePreview;
